/**
 * LumaPanel — Beacon's AI assistant. Floating launcher + right slide-out
 * chat. Luma proposes plan changes as action cards; each executes against
 * the existing plan endpoints only after the user confirms. Also reviews
 * generated content (packs / year packs) — triggered from the Library or
 * by asking in chat.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

import { API } from "../lib/beaconUtils";

const ACTION_LABELS = {
  replace_theme: (a) => `Replace ${a.month}'s theme — "${a.brief}"`,
  custom_topic: (a) => `Build custom topic for ${a.slot} — "${a.brief}"`,
  blank_week: (a) => `Blank ${a.slot} (no comms)`,
  restore_week: (a) => `Restore ${a.slot} to its base topic`,
};

const BRIEF_RE = /---\s*\**\s*BRIEF\s*\**\s*---([\s\S]*?)---\s*\**\s*END\s*BRIEF\s*\**\s*---/i;

// Minimal **bold** renderer — Luma's reviews use bold section headings.
function renderInline(text) {
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
}

function BriefCard({ brief, onRebuild }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(brief);
      setCopied(true);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = brief;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="beacon-luma-brief" data-testid="luma-brief-card">
      <div className="beacon-luma-brief-head">
        <span>REGENERATION BRIEF</span>
        <span className="beacon-luma-brief-btns">
          {onRebuild && (
            <button type="button" className="beacon-luma-brief-copy is-solid"
              data-testid="luma-brief-rebuild"
              onClick={() => onRebuild(brief)}>
              Rebuild →
            </button>
          )}
          <button type="button" className="beacon-luma-brief-copy"
            data-testid="luma-brief-copy" onClick={copy}>
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </span>
      </div>
      <pre className="beacon-luma-brief-text">{brief}</pre>
    </div>
  );
}

function MessageContent({ content, onRebuild }) {
  const m = content.match(BRIEF_RE);
  if (!m) return renderInline(content);
  const before = content.slice(0, m.index).trim();
  const after = content.slice(m.index + m[0].length).trim();
  return (
    <>
      {renderInline(before)}
      <BriefCard brief={m[1].trim()} onRebuild={onRebuild} />
      {after && renderInline(after)}
    </>
  );
}

export default function LumaPanel({
  activeView, planId, profile, reviewRequest, onReviewHandled,
  onRebuildBrief,
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const bodyRef = useRef(null);
  const [sessionId] = useState(() => {
    const key = "beacon-luma-session";
    let sid = window.localStorage.getItem(key);
    if (!sid) {
      sid = `luma_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(key, sid);
    }
    return sid;
  });

  // Allow other surfaces (Help page, score popover) to open the panel.
  useEffect(() => {
    const openPanel = () => setOpen(true);
    window.addEventListener("beacon-open-luma", openPanel);
    return () => window.removeEventListener("beacon-open-luma", openPanel);
  }, []);

  const historyLoaded = useRef(false);
  useEffect(() => {
    if (!open || historyLoaded.current) return;
    historyLoaded.current = true;
    axios.get(`${API}/luma/history`, { params: { session_id: sessionId } })
      .then((r) => setMessages((m) => (m.length ? m : r.data.messages)))
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, busy]);

  const sendMessage = async (text, extra = {}) => {
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/luma/chat`, {
        session_id: sessionId,
        message: text,
        plan_id: planId,
        view: activeView,
        site_name: profile?.site_name || null,
        industry: profile?.industry || null,
        jurisdictions: profile?.jurisdictions || null,
        free_text_context: profile?.free_text_context || null,
        ...extra,
      });
      setMessages((m) => [...m, {
        role: "assistant", content: r.data.reply, actions: r.data.actions,
        reviewed_pack_id: r.data.reviewed_pack_id,
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Sorry — that didn't go through (${e.response?.data?.detail || e.message}). Try again.`,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendMessage(text);
  };

  // Review requests arrive from the Library / Annual Plan "Review with
  // Luma" buttons — open the panel and auto-send a review message with
  // the pack id attached so the backend injects its content.
  useEffect(() => {
    if (!reviewRequest) return;
    setOpen(true);
    const { kind, id, title } = reviewRequest;
    sendMessage(
      `Review "${title}" for quality and compliance.`,
      kind === "year"
        ? { review_year_pack_id: id }
        : { review_pack_id: id },
    );
    onReviewHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewRequest]);

  const runAction = async (action, key) => {
    setActionBusy(key);
    const prof = {
      industry: profile?.industry || null,
      jurisdictions: profile?.jurisdictions || null,
      free_text_context: profile?.free_text_context || null,
    };
    try {
      if (action.type === "replace_theme") {
        await axios.post(`${API}/plan/replace-theme`, {
          plan_id: planId, month: action.month, mode: "custom",
          brief: action.brief, reason: "Luma", ...prof,
        });
      } else if (action.type === "custom_topic") {
        await axios.post(`${API}/plan/custom-topic`, {
          plan_id: planId, slot: action.slot, brief: action.brief,
          reason: "Luma", ...prof,
        });
      } else if (action.type === "blank_week") {
        await axios.post(`${API}/plan/override`, {
          plan_id: planId, slot: action.slot,
          override_topic_id: "no-comms", reason: "Luma",
        });
      } else if (action.type === "restore_week") {
        const r = await axios.get(`${API}/plan`, {
          params: { plan_id: planId },
        });
        const s = r.data.slots.find((x) => x.slot === action.slot);
        await axios.post(`${API}/plan/override`, {
          plan_id: planId, slot: action.slot,
          override_topic_id: s.base.topic_id, reason: "Luma restore",
        });
      }
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Done — ${ACTION_LABELS[action.type](action)}. Check the Annual Plan to see it.`,
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: `That change failed: ${e.response?.data?.detail || e.message}`,
      }]);
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <>
      {!open && (
        <button type="button" className="beacon-luma-fab"
          data-testid="luma-launcher"
          onClick={() => setOpen(true)}>
          ✦ Luma
        </button>
      )}
      {open && (
        <aside className="beacon-luma-panel" data-testid="luma-panel">
          <div className="beacon-luma-head">
            <div>
              <strong>✦ Luma</strong>
              <span className="beacon-luma-sub">
                Your safety comms offsider
              </span>
            </div>
            <button type="button" className="beacon-luma-close"
              data-testid="luma-close" onClick={() => setOpen(false)}>×</button>
          </div>
          <div className="beacon-luma-body" ref={bodyRef}
            data-testid="luma-messages">
            {messages.length === 0 && !busy && (
              <div className="beacon-luma-msg is-assistant">
                G&rsquo;day — I&rsquo;m Luma. Ask me to shape your annual
                calendar, swap a theme, build a custom week — or to review
                a pack you&rsquo;ve already built for quality and
                compliance (try &ldquo;review my latest toolbox
                talk&rdquo;). I&rsquo;ll always check with you before
                changing anything.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i}
                className={`beacon-luma-msg is-${m.role === "user" ? "user" : "assistant"}`}>
                <MessageContent content={m.content}
                  onRebuild={m.reviewed_pack_id && onRebuildBrief
                    ? (brief) => {
                        setOpen(false);
                        onRebuildBrief({
                          packId: m.reviewed_pack_id, brief,
                        });
                      }
                    : null} />
                {(m.actions || []).map((a, j) => (
                  <div key={j} className="beacon-luma-action"
                    data-testid={`luma-action-${i}-${j}`}>
                    <span>{ACTION_LABELS[a.type]?.(a) || a.type}</span>
                    <button type="button" className="beacon-cta beacon-cta-compact"
                      data-testid={`luma-action-confirm-${i}-${j}`}
                      disabled={actionBusy !== null}
                      onClick={() => runAction(a, `${i}-${j}`)}>
                      {actionBusy === `${i}-${j}` ? "Applying…" : "Confirm →"}
                    </button>
                  </div>
                ))}
              </div>
            ))}
            {busy && (
              <div className="beacon-luma-msg is-assistant beacon-luma-typing">
                Luma is thinking…
              </div>
            )}
          </div>
          <div className="beacon-luma-input">
            <input className="beacon-input" value={input}
              data-testid="luma-input"
              placeholder="Ask Luma…"
              maxLength={2000}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} />
            <button type="button" className="beacon-cta beacon-cta-compact"
              data-testid="luma-send"
              disabled={busy || !input.trim()}
              onClick={send}>→</button>
          </div>
        </aside>
      )}
    </>
  );
}
